import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Link from 'components/Link';
import Picture from 'components/Picture';
import { person as personPropType } from 'lib/CustomPropTypes';
import { sanitize } from 'lib/social';
import get from 'lodash.get';
import AIMS_FLAVORS from 'lib/aimsFlavors';

const getImage = (img, authorUrl) => {
  if (img) {
    const { altText, url: { primary: src } = {} } = img;
    const shouldLinkImage = !!authorUrl;
    return (
      <span
        data-test="article-byline__headshot"
        className="article-byline__image article-byline__image article-byline__image--has-image mr4 article-byline__headshot w3-print"
      >
        {
          shouldLinkImage
            ? (
              <a href={authorUrl}>
                <Picture
                  alt={altText}
                  url={src}
                  responsiveFlavors={{ s: AIMS_FLAVORS.FOCAL_100x100 }}
                />
              </a>
            )
            : (
              <Picture
                alt={altText}
                url={src}
                responsiveFlavors={{ s: AIMS_FLAVORS.FOCAL_100x100 }}
              />
            )
        }
      </span>
    );
  }
  return <span className="article-byline__image article-byline__image--no-image mt3 mv6-m mr4 mr0-print" data-test="article-byline__img" />;
};

const getName = (name, authorUrl, isRecipe) => {
  const shouldLinkName = !!authorUrl;
  const authorClassNames = classNames(
    'article-byline__name',
    'article-byline__name',
    'ml9 ml0-m ml0-print',
    {
      'founders-mono f3 lh-title ls-tight pt1 pt4-m': !isRecipe,
      'founders-cond f6 lh-none fw6 article-byline__name--recipe black-print': isRecipe,
    },
  );

  return (
    <span
      className={authorClassNames}
    >
      {
        shouldLinkName
          ? (<a href={authorUrl}>{name}</a>)
          : (name)
      }
    </span>
  );
};

const getSocial = (social, email = null) => (
  <span
    className="article-byline__social f2 mt2 mt4-m ml9 ml0-m dn-print"
    data-test="article-byline__social"
  >
    {typeof social !== 'undefined' && social.twitter && (
      <Link
        href={`https://twitter.com/${sanitize(social.twitter)}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(event) => { event.stopPropagation(); }}
        className="article-byline__social-link"
      >
        <span className="icon icon-twitter" />
      </Link>
    )}
    {typeof social !== 'undefined' && social.facebook && (
      <Link
        href={`https://facebook.com/${sanitize(social.facebook)}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(event) => { event.stopPropagation(); }}
        className="article-byline__social-link"
      >
        <span className="icon icon-facebook" />
      </Link>
    )}
    {email && (
      <Link
        href={`mailto:${email.trim()}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(event) => { event.stopPropagation(); }}
        className="article-byline__social-link"
      >
        <span className="icon icon-email" />
      </Link>
    )}
  </span>
);

const getBio = (bios) => bios.map((bio, index) => {
  if (bio && bio.type && bio.type === 'markup') {
    const key = `bio-${index}`;
    const {
      html,
      element: Element,
    } = bio;
    return (
      <Element
        key={key}
        dangerouslySetInnerHTML={{ __html: html }}
        className="article-byline__bio article-byline__bio publico-txt f2 lh-copy mt3 mt0-m ml9-m"
      />
    );
  }
  // TODO: are there other types?
  return null;
});

const Byline = ({ person, isRecipeByline, containerClass }) => {
  const {
    name,
    email,
    bio,
    primaryImage: img,
    socialMediaProfiles: social,
  } = person;

  const authorUrl = get(person, ['url', 'primary'], undefined);

  return (
    <>
      <div
        className={classNames(
          containerClass,
          'article-byline pt3',
          {
            'article-byline--recipe b-none-print pt0-print': isRecipeByline,
          },
        )}
      >
        <div
          className={classNames(
            'article-byline__inner mt0-m',
            {
              mt2: !isRecipeByline,
              'items-center-m': isRecipeByline,
            },
          )}
        >
          {getImage(img, authorUrl)}
          {getName(name, authorUrl, isRecipeByline)}
          {!isRecipeByline && getSocial(social, email)}
        </div>
        {bio && !isRecipeByline && getBio(bio)}
      </div>
    </>
  );
};

Byline.propTypes = {
  person: personPropType.isRequired,
  isRecipeByline: PropTypes.bool,
  containerClass: PropTypes.string,
};

Byline.defaultProps = {
  isRecipeByline: false,
  containerClass: '',
};

export default Byline;
